import type { RecorderController } from './RecorderController';
import type { RecordingMomentPermit, RecordingMomentRequest } from './RecordingSegmentController';
import type {
  PendingRecordingSnapshot,
  RecordingSnapshotServerApi,
  RecordingSnapshotStore,
} from './types';

const MAX_AUTOMATIC_ATTEMPTS = 5;
const REQUEST_TIMEOUT_MS = 12_000;
const RECOVERY_CONCURRENCY = 4;
const POLICY_COALESCE_MS = 1_250;
const RETRY_DELAYS_MS = [1_000, 2_500, 5_000, 10_000, 20_000] as const;
const NON_RETRYABLE_ERROR_CODES = new Set([
  'payload_too_large',
  'snapshot_too_large',
  'capture_forbidden_for_origin',
  'snapshot_count_limit',
  'snapshot_storage_limit',
  'snapshot_request_conflict',
  'snapshot_timeline_invalid',
  'FREE_MONTHLY_CAP_REACHED',
]);

export type RecordingSnapshotUploadSnapshot = {
  ownerId: string | null;
  pending: number;
  failed: number;
  uploading: number;
  items: Array<Pick<PendingRecordingSnapshot,
    'key' | 'requestId' | 'trigger' | 'state' | 'attempts' | 'lastErrorCode'> & { retryable: boolean }>;
};

export type RecordingSnapshotControllerDependencies = {
  store: RecordingSnapshotStore;
  server: RecordingSnapshotServerApi;
  getAuthenticatedOwnerId: () => string | null | undefined;
  now?: () => Date;
  sourceUrl?: () => string;
  lockManager?: Pick<LockManager, 'request'>;
};

const INITIAL: RecordingSnapshotUploadSnapshot = {
  ownerId: null,
  pending: 0,
  failed: 0,
  uploading: 0,
  items: [],
};

function errorCode(error: unknown): string {
  if (error && typeof error === 'object' && 'status' in error && error.status === 413) {
    return 'payload_too_large';
  }
  if (error && typeof error === 'object' && 'code' in error && typeof error.code === 'string') {
    return error.code;
  }
  if (error instanceof DOMException && error.name === 'AbortError') return 'timeout';
  return 'upload_failed';
}

export class RecordingSnapshotController {
  private readonly listeners = new Set<() => void>();
  private readonly retryTimers = new Map<string, ReturnType<typeof setTimeout>>();
  private readonly abortControllers = new Map<string, AbortController>();
  private readonly policyPreflights = new Map<string, { recordingId: string; controller: AbortController }>();
  private readonly discardedRecordings = new Set<string>();
  private readonly localLocks = new Map<string, Promise<void>>();
  private readonly policyFailureCounts = new Map<string, number>();
  private readonly retryPolicies = new Map<string, {
    promise: Promise<'full' | 'metadata_only' | 'never'>;
    validUntil: number;
  }>();
  private readonly preparedPolicies = new Map<string, {
    recordingId: string;
    mode: 'full' | 'metadata_only' | 'never';
    expiresAt: number;
  }>();
  private snapshot: RecordingSnapshotUploadSnapshot = INITIAL;
  private generation = 0;

  constructor(
    private readonly recorder: RecorderController,
    private readonly dependencies: RecordingSnapshotControllerDependencies,
  ) {}

  subscribe = (listener: () => void): (() => void) => {
    this.listeners.add(listener);
    return () => this.listeners.delete(listener);
  };

  getSnapshot = (): RecordingSnapshotUploadSnapshot => this.snapshot;

  private publish(next: RecordingSnapshotUploadSnapshot): void {
    this.snapshot = next;
    this.listeners.forEach((listener) => listener());
  }

  private async refresh(ownerId: string | null = this.snapshot.ownerId): Promise<void> {
    if (!ownerId) {
      this.publish(INITIAL);
      return;
    }
    const rows = await this.dependencies.store.listSnapshotsForOwner(ownerId);
    if (this.snapshot.ownerId !== ownerId) return;
    this.publish({
      ownerId,
      pending: rows.filter((row) => row.state === 'pending').length,
      failed: rows.filter((row) => row.state === 'failed').length,
      uploading: rows.filter((row) => row.state === 'uploading').length,
      items: rows.map((row) => ({
        key: row.key,
        requestId: row.requestId,
        trigger: row.trigger,
        state: row.state,
        attempts: row.attempts,
        lastErrorCode: row.lastErrorCode,
        retryable: !NON_RETRYABLE_ERROR_CODES.has(row.lastErrorCode ?? ''),
      })),
    });
  }

  private async runExclusive<T>(key: string, operation: () => Promise<T>): Promise<T> {
    const previous = this.localLocks.get(key) ?? Promise.resolve();
    let release!: () => void;
    const current = new Promise<void>((resolve) => { release = resolve; });
    this.localLocks.set(key, current);
    await previous;
    const locks = this.dependencies.lockManager ?? globalThis.navigator?.locks;
    try {
      if (!locks) return await operation();
      return await locks.request(`vd-recorder-snapshot:${key}`, { mode: 'exclusive' }, operation);
    } finally {
      release();
      if (this.localLocks.get(key) === current) this.localLocks.delete(key);
    }
  }

  private foregroundCaptureActive(generation: number, ownerId: string, recordingId: string): boolean {
    const currentSession = this.recorder.getSnapshot().session;
    return generation === this.generation
      && this.snapshot.ownerId === ownerId
      && this.dependencies.getAuthenticatedOwnerId() === ownerId
      && !this.discardedRecordings.has(recordingId)
      && currentSession?.id === recordingId
      && currentSession.ownerId === ownerId;
  }

  private retryActive(generation: number, ownerId: string, recordingId: string): boolean {
    return generation === this.generation
      && this.snapshot.ownerId === ownerId
      && this.dependencies.getAuthenticatedOwnerId() === ownerId
      && !this.discardedRecordings.has(recordingId);
  }

  private shouldPurge(ownerId: string, recordingId: string): boolean {
    return this.snapshot.ownerId !== ownerId
      || this.dependencies.getAuthenticatedOwnerId() !== ownerId
      || this.discardedRecordings.has(recordingId);
  }

  private async removeForegroundWriteIfStale(
    key: string,
    generation: number,
    ownerId: string,
    recordingId: string,
  ): Promise<boolean> {
    if (this.foregroundCaptureActive(generation, ownerId, recordingId)) return false;
    await this.dependencies.store.deleteSnapshot(key);
    return true;
  }

  private async removeRetryWriteIfSensitiveStateChanged(
    key: string,
    generation: number,
    ownerId: string,
    recordingId: string,
  ): Promise<boolean> {
    if (this.retryActive(generation, ownerId, recordingId)) return false;
    if (this.shouldPurge(ownerId, recordingId)) await this.dependencies.store.deleteSnapshot(key);
    return true;
  }

  private retryPolicy(recordingId: string): Promise<'full' | 'metadata_only' | 'never'> {
    const current = this.retryPolicies.get(recordingId);
    if (current && (current.validUntil === Number.POSITIVE_INFINITY || current.validUntil > Date.now())) {
      return current.promise;
    }
    const entry: {
      promise: Promise<'full' | 'metadata_only' | 'never'>;
      validUntil: number;
    } = {
      validUntil: Number.POSITIVE_INFINITY,
      promise: Promise.resolve('never' as const),
    };
    entry.promise = this.requestPolicy(recordingId, 'upload').then((mode) => {
      entry.validUntil = Date.now() + POLICY_COALESCE_MS;
      return mode;
    }).catch((error) => {
      if (this.retryPolicies.get(recordingId) === entry) this.retryPolicies.delete(recordingId);
      throw error;
    });
    this.retryPolicies.set(recordingId, entry);
    return entry.promise;
  }

  private async processRows(
    rows: PendingRecordingSnapshot[],
    ownerId: string,
    generation: number,
  ): Promise<void> {
    let nextIndex = 0;
    const worker = async () => {
      while (nextIndex < rows.length) {
        const row = rows[nextIndex++];
        if (row) await this.process(row.key, ownerId, generation, false);
      }
    };
    await Promise.all(Array.from(
      { length: Math.min(RECOVERY_CONCURRENCY, rows.length) },
      () => worker(),
    ));
  }

  private async requestPolicy(
    recordingId: string,
    purpose: 'capture' | 'upload',
  ): Promise<'full' | 'metadata_only' | 'never'> {
    const abort = new AbortController();
    const preflightId = globalThis.crypto.randomUUID();
    this.policyPreflights.set(preflightId, { recordingId, controller: abort });
    const timer = setTimeout(() => abort.abort(
      new DOMException('Snapshot policy preflight timed out.', 'AbortError'),
    ), REQUEST_TIMEOUT_MS);
    try {
      const result = await this.dependencies.server.preflight(recordingId, abort.signal, purpose);
      return result.captureMode;
    } finally {
      clearTimeout(timer);
      this.policyPreflights.delete(preflightId);
    }
  }

  async prepareMoment(recordingId: string): Promise<RecordingMomentPermit> {
    const captureMode = await this.requestPolicy(recordingId, 'capture');
    const token = globalThis.crypto.randomUUID();
    const currentTime = Date.now();
    for (const [preparedToken, prepared] of this.preparedPolicies) {
      if (prepared.expiresAt <= currentTime) this.preparedPolicies.delete(preparedToken);
    }
    this.preparedPolicies.set(token, {
      recordingId,
      mode: captureMode,
      // This permit is intentionally a bounded point-in-time decision. The
      // coordinated segment mutation may take several requests, so profile
      // changes do not revoke it mid-action. process() passes the upload gate,
      // and both that gate and the API persistence check clear or reject pixels
      // if policy became never.
      expiresAt: currentTime + 2 * 60_000,
    });
    return { recordingId, token };
  }

  releaseMoment(permit: RecordingMomentPermit | undefined): void {
    if (permit) this.preparedPolicies.delete(permit.token);
  }

  private consumePermit(
    recordingId: string,
    permit: RecordingMomentPermit,
  ): 'full' | 'metadata_only' | 'never' {
    const prepared = this.preparedPolicies.get(permit.token);
    this.preparedPolicies.delete(permit.token);
    if (!prepared || prepared.expiresAt <= Date.now()
      || permit.recordingId !== recordingId || prepared.recordingId !== recordingId) {
      throw new Error('Snapshot policy permit is invalid, expired, or was already consumed.');
    }
    return prepared.mode;
  }

  async capture(
    request: RecordingMomentRequest,
    preparedPermit?: RecordingMomentPermit,
  ): Promise<'captured' | 'blocked'> {
    const captureGeneration = this.generation;
    const session = this.recorder.getSnapshot().session;
    const authenticatedOwnerId = this.dependencies.getAuthenticatedOwnerId();
    if (!session || session.id !== request.recordingId
      || !authenticatedOwnerId || authenticatedOwnerId !== session.ownerId) {
      throw new Error('Snapshot ownership changed while capturing.');
    }
    if (this.discardedRecordings.has(request.recordingId)) {
      throw new Error('Recording was discarded while capturing.');
    }
    const key = `${request.recordingId}:${request.requestId}`;
    const permit = preparedPermit ?? await this.prepareMoment(request.recordingId);
    const captureMode = this.consumePermit(request.recordingId, permit);
    if (!this.foregroundCaptureActive(captureGeneration, session.ownerId, request.recordingId)) {
      return 'blocked';
    }
    const existing = await this.dependencies.store.getSnapshot(key);
    if (!this.foregroundCaptureActive(captureGeneration, session.ownerId, request.recordingId)) {
      return 'blocked';
    }
    if (existing) {
      if (existing.ownerId !== session.ownerId
        || existing.segmentId !== request.segmentId
        || existing.capturedAtMs !== request.capturedAtMs
        || existing.trigger !== request.trigger) {
        throw new Error('Snapshot request was reused with different metadata.');
      }
      if (captureMode === 'never') {
        const blocked = {
          ...existing,
          sourceUrl: '',
          screenshot: new Blob([], { type: 'image/png' }),
          thumbnail: new Blob([], { type: 'image/webp' }),
          state: 'failed' as const,
          updatedAt: (this.dependencies.now ?? (() => new Date()))().toISOString(),
          lastErrorCode: 'capture_forbidden_for_origin',
        };
        if (!this.foregroundCaptureActive(captureGeneration, session.ownerId, request.recordingId)) {
          return 'blocked';
        }
        await this.dependencies.store.putSnapshot(blocked);
        if (await this.removeForegroundWriteIfStale(
          key, captureGeneration, session.ownerId, request.recordingId,
        )) return 'blocked';
        await this.refresh(session.ownerId);
        return 'blocked';
      }
      if (captureMode === 'metadata_only'
        && (existing.screenshot.size > 0 || existing.thumbnail.size > 0)) {
        if (!this.foregroundCaptureActive(captureGeneration, session.ownerId, request.recordingId)) {
          return 'blocked';
        }
        await this.dependencies.store.putSnapshot({
          ...existing,
          screenshot: new Blob([], { type: 'image/png' }),
          thumbnail: new Blob([], { type: 'image/webp' }),
        });
        if (await this.removeForegroundWriteIfStale(
          key, captureGeneration, session.ownerId, request.recordingId,
        )) return 'blocked';
      }
      if (existing.lastErrorCode === 'capture_forbidden_for_origin') return 'blocked';
      const outcome = await this.process(key, session.ownerId, captureGeneration);
      return outcome === 'blocked' ? 'blocked' : 'captured';
    }

    // Resolve the server-owned effective profile before the display API is
    // touched. This is a privacy boundary, not an optimization: a denied
    // origin must never place pixels in memory, IndexedDB, or an HTTP body.
    if (captureMode === 'never') {
      const now = (this.dependencies.now ?? (() => new Date()))().toISOString();
      if (!this.foregroundCaptureActive(captureGeneration, session.ownerId, request.recordingId)) {
        return 'blocked';
      }
      await this.dependencies.store.putSnapshot({
        key,
        ownerId: session.ownerId,
        recordingId: session.id,
        segmentId: request.segmentId,
        projectId: session.projectId,
        requestId: request.requestId,
        capturedAtMs: request.capturedAtMs,
        trigger: request.trigger,
        sourceUrl: '',
        screenshot: new Blob([], { type: 'image/png' }),
        thumbnail: new Blob([], { type: 'image/webp' }),
        state: 'failed',
        attempts: 0,
        createdAt: now,
        updatedAt: now,
        expiresAt: session.expiresAt,
        lastErrorCode: 'capture_forbidden_for_origin',
      });
      if (await this.removeForegroundWriteIfStale(
        key, captureGeneration, session.ownerId, request.recordingId,
      )) return 'blocked';
      await this.refresh(session.ownerId);
      return 'blocked';
    }

    const frame = captureMode === 'full'
      ? await this.recorder.captureDisplayFrame()
      : {
        screenshot: new Blob([], { type: 'image/png' }),
        thumbnail: new Blob([], { type: 'image/webp' }),
      };
    if (!this.foregroundCaptureActive(captureGeneration, session.ownerId, request.recordingId)) {
      return 'blocked';
    }
    const now = (this.dependencies.now ?? (() => new Date()))().toISOString();
    const row: PendingRecordingSnapshot = {
      key,
      ownerId: session.ownerId,
      recordingId: session.id,
      segmentId: request.segmentId,
      projectId: session.projectId,
      requestId: request.requestId,
      capturedAtMs: request.capturedAtMs,
      trigger: request.trigger,
      sourceUrl: (this.dependencies.sourceUrl ?? (() => window.location.href.split('#')[0]!))(),
      screenshot: frame.screenshot,
      thumbnail: frame.thumbnail,
      state: 'pending',
      attempts: 0,
      createdAt: now,
      updatedAt: now,
      expiresAt: session.expiresAt,
    };
    if (!this.foregroundCaptureActive(captureGeneration, session.ownerId, request.recordingId)) {
      return 'blocked';
    }
    await this.dependencies.store.putSnapshot(row);
    if (await this.removeForegroundWriteIfStale(
      key, captureGeneration, session.ownerId, request.recordingId,
    )) return 'blocked';
    await this.refresh(session.ownerId);
    if (!this.foregroundCaptureActive(captureGeneration, session.ownerId, request.recordingId)) {
      await this.dependencies.store.deleteSnapshot(key);
      return 'blocked';
    }
    const outcome = await this.process(key, session.ownerId, captureGeneration);
    return outcome === 'blocked' ? 'blocked' : 'captured';
  }

  async resumeOwner(ownerId: string): Promise<void> {
    this.generation++;
    this.snapshot = { ...INITIAL, ownerId };
    await this.dependencies.store.pruneExpiredSnapshots((this.dependencies.now ?? (() => new Date()))());
    const rows = await this.dependencies.store.listSnapshotsForOwner(ownerId);
    const now = (this.dependencies.now ?? (() => new Date()))().toISOString();
    await Promise.all(rows.map(async (row) => {
      if (row.state !== 'uploading') return;
      await this.dependencies.store.putSnapshot({ ...row, state: 'failed', updatedAt: now, lastErrorCode: 'interrupted' });
    }));
    await this.refresh(ownerId);
    const generation = this.generation;
    const current = await this.dependencies.store.listSnapshotsForOwner(ownerId);
    await this.processRows(current, ownerId, generation);
  }

  retryAll(): void {
    const ownerId = this.snapshot.ownerId;
    if (!ownerId) return;
    const generation = this.generation;
    void this.dependencies.store.listSnapshotsForOwner(ownerId)
      .then((rows) => this.processRows(rows, ownerId, generation));
  }

  retrySnapshot(key: string): void {
    const ownerId = this.snapshot.ownerId;
    const visible = this.snapshot.items.some((item) => item.key === key
      && item.state === 'failed'
      && item.retryable);
    if (!ownerId || !visible) return;
    const timer = this.retryTimers.get(key);
    if (timer) clearTimeout(timer);
    this.retryTimers.delete(key);
    void this.process(key, ownerId, this.generation, true);
  }

  async prepareDiscard(recordingId: string): Promise<void> {
    this.discardedRecordings.add(recordingId);
    for (const preflight of this.policyPreflights.values()) {
      if (preflight.recordingId === recordingId) {
        preflight.controller.abort(new DOMException('Recording discarded.', 'AbortError'));
      }
    }
    for (const [token, prepared] of this.preparedPolicies) {
      if (prepared.recordingId === recordingId) this.preparedPolicies.delete(token);
    }
    this.retryPolicies.delete(recordingId);
    for (const key of this.policyFailureCounts.keys()) {
      if (key.startsWith(`${recordingId}:`)) this.policyFailureCounts.delete(key);
    }
    for (const [key, timer] of this.retryTimers) {
      if (!key.startsWith(`${recordingId}:`)) continue;
      clearTimeout(timer);
      this.retryTimers.delete(key);
    }
    for (const [key, controller] of this.abortControllers) {
      if (!key.startsWith(`${recordingId}:`)) continue;
      controller.abort(new DOMException('Recording discarded.', 'AbortError'));
    }
    const rows = await this.dependencies.store.listSnapshotsForRecording(recordingId);
    await Promise.all(rows.map((row) => this.runExclusive(row.key, async () => {
      await this.dependencies.store.deleteSnapshot(row.key);
    })));
    await this.dependencies.store.deleteSnapshotsForRecording(recordingId);
    await this.refresh();
  }

  async prepareForSignOut(): Promise<void> {
    const ownerId = this.snapshot.ownerId;
    this.generation++;
    this.retryTimers.forEach(clearTimeout);
    this.retryTimers.clear();
    this.abortControllers.forEach((controller) => controller.abort());
    this.abortControllers.clear();
    this.policyPreflights.forEach(({ controller }) => controller.abort());
    this.policyPreflights.clear();
    this.preparedPolicies.clear();
    this.retryPolicies.clear();
    this.policyFailureCounts.clear();
    this.discardedRecordings.clear();
    if (ownerId) {
      const rows = await this.dependencies.store.listSnapshotsForOwner(ownerId);
      await Promise.all(rows.map((row) => this.runExclusive(row.key, async () => {
        await this.dependencies.store.deleteSnapshot(row.key);
      })));
      await this.dependencies.store.deleteSnapshotsForOwner(ownerId);
    }
    this.publish(INITIAL);
  }

  clearOwner(): void {
    this.generation++;
    this.retryTimers.forEach(clearTimeout);
    this.retryTimers.clear();
    this.abortControllers.forEach((controller) => controller.abort());
    this.abortControllers.clear();
    this.policyPreflights.forEach(({ controller }) => controller.abort());
    this.policyPreflights.clear();
    this.preparedPolicies.clear();
    this.retryPolicies.clear();
    this.policyFailureCounts.clear();
    this.discardedRecordings.clear();
    this.publish(INITIAL);
  }

  dispose(): void {
    this.clearOwner();
    this.listeners.clear();
  }

  private schedule(row: PendingRecordingSnapshot, generation: number, delayOverride?: number): void {
    const policyFailure = row.lastErrorCode === 'policy_preflight_failed'
      || row.lastErrorCode === 'policy_rate_limited';
    if ((!policyFailure && row.attempts >= MAX_AUTOMATIC_ATTEMPTS)
      || NON_RETRYABLE_ERROR_CODES.has(row.lastErrorCode ?? '')
      || this.retryTimers.has(row.key)) return;
    const policyFailures = this.policyFailureCounts.get(row.key) ?? 0;
    const delay = delayOverride ?? RETRY_DELAYS_MS[Math.min(
      policyFailure ? policyFailures : row.attempts,
      RETRY_DELAYS_MS.length - 1,
    )]!;
    const timer = setTimeout(() => {
      this.retryTimers.delete(row.key);
      void this.process(row.key, row.ownerId, generation, false);
    }, delay);
    this.retryTimers.set(row.key, timer);
  }

  private async process(
    key: string,
    ownerId: string,
    generation: number,
    explicit = false,
  ): Promise<'uploaded' | 'blocked' | 'skipped'> {
    return this.runExclusive(key, async () => {
      if (generation !== this.generation || this.snapshot.ownerId !== ownerId) return 'skipped';
      let row = await this.dependencies.store.getSnapshot(key);
      if (!row || row.ownerId !== ownerId) return 'skipped';
      if (this.discardedRecordings.has(row.recordingId)) return 'skipped';
      if (!explicit && NON_RETRYABLE_ERROR_CODES.has(row.lastErrorCode ?? '')) return 'skipped';
      if (!explicit && row.attempts >= MAX_AUTOMATIC_ATTEMPTS) return 'skipped';
      const authenticatedOwnerId = this.dependencies.getAuthenticatedOwnerId();
      if (authenticatedOwnerId !== ownerId) return 'skipped';
      const timestamp = (this.dependencies.now ?? (() => new Date()))().toISOString();
      let retryMode: 'full' | 'metadata_only' | 'never';
      try {
        retryMode = await this.retryPolicy(row.recordingId);
        this.policyFailureCounts.delete(key);
      } catch (error) {
        if (!this.retryActive(generation, ownerId, row.recordingId)) return 'skipped';
        const status = error && typeof error === 'object' && 'status' in error
          ? Number(error.status)
          : undefined;
        const retryAfterMs = error && typeof error === 'object' && 'retryAfterMs' in error
          ? Number(error.retryAfterMs)
          : undefined;
        this.policyFailureCounts.set(key, (this.policyFailureCounts.get(key) ?? 0) + 1);
        const failed = {
          ...row,
          state: 'failed' as const,
          attempts: row.attempts,
          updatedAt: timestamp,
          lastErrorCode: status === 429 ? 'policy_rate_limited' : 'policy_preflight_failed',
        };
        await this.dependencies.store.putSnapshot(failed);
        if (await this.removeRetryWriteIfSensitiveStateChanged(
          key, generation, ownerId, row.recordingId,
        )) return 'skipped';
        this.schedule(failed, generation, Number.isFinite(retryAfterMs) && retryAfterMs! > 0
          ? retryAfterMs
          : undefined);
        await this.refresh(ownerId);
        return 'skipped';
      }
      if (!this.retryActive(generation, ownerId, row.recordingId)) return 'skipped';
      if (retryMode === 'never') {
        await this.dependencies.store.putSnapshot({
          ...row,
          sourceUrl: '',
          screenshot: new Blob([], { type: 'image/png' }),
          thumbnail: new Blob([], { type: 'image/webp' }),
          state: 'failed',
          attempts: row.attempts + 1,
          updatedAt: timestamp,
          lastErrorCode: 'capture_forbidden_for_origin',
        });
        if (await this.removeRetryWriteIfSensitiveStateChanged(
          key, generation, ownerId, row.recordingId,
        )) return 'skipped';
        await this.refresh(ownerId);
        return 'blocked';
      }
      if (retryMode === 'metadata_only') {
        row = {
          ...row,
          screenshot: new Blob([], { type: 'image/png' }),
          thumbnail: new Blob([], { type: 'image/webp' }),
        };
      }
      row = {
        ...row,
        state: 'uploading',
        attempts: row.attempts + 1,
        updatedAt: timestamp,
        lastErrorCode: undefined,
      };
      if (!this.retryActive(generation, ownerId, row.recordingId)) return 'skipped';
      await this.dependencies.store.putSnapshot(row);
      if (await this.removeRetryWriteIfSensitiveStateChanged(
        key, generation, ownerId, row.recordingId,
      )) return 'skipped';
      await this.refresh(ownerId);
      if (await this.removeRetryWriteIfSensitiveStateChanged(
        key, generation, ownerId, row.recordingId,
      )) return 'skipped';
      const abort = new AbortController();
      this.abortControllers.set(key, abort);
      const timeout = setTimeout(() => abort.abort(new DOMException('Snapshot upload timed out.', 'AbortError')), REQUEST_TIMEOUT_MS);
      try {
        const response = await this.dependencies.server.upload(row, abort.signal);
        if (!this.retryActive(generation, ownerId, row.recordingId)) return 'skipped';
        if (response.uploadStatus === 'pending') {
          const pending = { ...row, state: 'pending' as const, updatedAt: timestamp };
          await this.dependencies.store.putSnapshot(pending);
          if (await this.removeRetryWriteIfSensitiveStateChanged(
            key, generation, ownerId, row.recordingId,
          )) return 'skipped';
          this.schedule(pending, generation);
        } else {
          await this.dependencies.store.deleteSnapshot(key);
          if (typeof window !== 'undefined') {
            window.dispatchEvent(new CustomEvent('vd:recorder-diagnostic', {
              detail: { code: 'snapshot_upload_ready', phase: 'snapshot' },
            }));
          }
        }
        return 'uploaded';
      } catch (error) {
        if (!this.retryActive(generation, ownerId, row.recordingId)) return 'skipped';
        const code = errorCode(error);
        const failed: PendingRecordingSnapshot = {
          ...row,
          // A policy denial is terminal. Retain only the request metadata so
          // no display pixels survive in IndexedDB or can be resent later.
          ...(code === 'capture_forbidden_for_origin' ? {
            sourceUrl: '',
            screenshot: new Blob([], { type: 'image/png' }),
            thumbnail: new Blob([], { type: 'image/webp' }),
          } : {}),
          state: 'failed',
          updatedAt: (this.dependencies.now ?? (() => new Date()))().toISOString(),
          lastErrorCode: code,
        };
        await this.dependencies.store.putSnapshot(failed);
        if (await this.removeRetryWriteIfSensitiveStateChanged(
          key, generation, ownerId, row.recordingId,
        )) return 'skipped';
        this.schedule(failed, generation);
        return code === 'capture_forbidden_for_origin' ? 'blocked' : 'skipped';
      } finally {
        clearTimeout(timeout);
        if (this.abortControllers.get(key) === abort) this.abortControllers.delete(key);
        await this.refresh(ownerId);
      }
    });
  }
}
